import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/ui-bits";
import { RichEditor } from "@/components/RichEditor";
import { toast } from "sonner";
import { Save, KeyRound, Loader2, ExternalLink, Plus, Trash2, Quote as QuoteIcon, RotateCcw, Mail as MailIcon, LogOut, PenLine, Users, Upload, Send, Image as ImageIcon } from "lucide-react";

export default function SettingsPage() {
    const [settings, setSettings] = useState(null);
    const [busy, setBusy] = useState(false);
    const [quotesBusy, setQuotesBusy] = useState(false);
    const [gmailStatus, setGmailStatus] = useState(null);
    const [gmailBusy, setGmailBusy] = useState(false);
    const [signature, setSignature] = useState("");
    const [signatureBusy, setSignatureBusy] = useState(false);
    const [form, setForm] = useState({
        sender_name: "",
        sender_email: "",
        default_signoff: "Best,",
        email_subject_template: "Red Fox Literary/{TITLE}",
        resend_api_key: "",
    });
    // quotes are edited in a separate panel with its own local state
    const [quotes, setQuotes] = useState([]);
    const [newQuote, setNewQuote] = useState({ text: "", author: "" });

    useEffect(() => {
        (async () => {
            const s = await api.get("/settings").then((r) => r.data);
            hydrate(s);
        })();
        refreshGmailStatus();

        // Listen for the OAuth popup postMessage so we can refresh status
        // when Gmail comes back connected without a full page reload.
        const onMsg = (ev) => {
            if (ev.data?.type === "gmail-oauth") {
                if (ev.data.ok) {
                    toast.success(`Gmail connected (${ev.data.email || "your account"})`);
                    refreshGmailStatus();
                } else {
                    toast.error(`Gmail connect failed: ${ev.data.reason || ""}`);
                }
            }
        };
        window.addEventListener("message", onMsg);
        return () => window.removeEventListener("message", onMsg);
    }, []);

    const refreshGmailStatus = async () => {
        try {
            const s = await api.get("/oauth/gmail/status").then((r) => r.data);
            setGmailStatus(s);
        } catch (e) {
            setGmailStatus({ connected: false });
        }
    };

    const connectGmail = async () => {
        setGmailBusy(true);
        try {
            const origin = window.location.origin;
            const { auth_url } = await api
                .get(`/oauth/gmail/login?redirect_host=${encodeURIComponent(origin)}`)
                .then((r) => r.data);
            // Open Google's consent screen in a popup; the callback page will
            // postMessage back to us and auto-close.
            const w = 520, h = 700;
            const left = window.screenX + (window.outerWidth - w) / 2;
            const top = window.screenY + (window.outerHeight - h) / 2;
            window.open(auth_url, "gmail-oauth", `width=${w},height=${h},left=${left},top=${top}`);
        } catch (e) {
            toast.error("Failed to start Gmail sign-in");
        } finally {
            setGmailBusy(false);
        }
    };

    const disconnectGmail = async () => {
        if (!window.confirm("Disconnect Gmail? You'll need to reauthorize before creating drafts again.")) return;
        setGmailBusy(true);
        try {
            await api.post("/oauth/gmail/disconnect");
            toast.success("Gmail disconnected");
            refreshGmailStatus();
        } catch (e) {
            toast.error("Failed to disconnect");
        } finally {
            setGmailBusy(false);
        }
    };

    const hydrate = (s) => {
        setSettings(s);
        setForm((f) => ({
            ...f,
            sender_name: s.sender_name || "",
            sender_email: s.sender_email || "",
            default_signoff: s.default_signoff || "Best,",
            email_subject_template: s.email_subject_template || "Red Fox Literary/{TITLE}",
        }));
        setQuotes(Array.isArray(s.quotes) ? s.quotes : []);
        setSignature(s.signature_html || "");
    };

    const save = async () => {
        setBusy(true);
        try {
            const patch = { ...form };
            if (!patch.resend_api_key) delete patch.resend_api_key;
            const s = await api.patch("/settings", patch).then((r) => r.data);
            hydrate(s);
            setForm((f) => ({ ...f, resend_api_key: "" }));
            toast.success("Settings saved");
        } catch (e) {
            toast.error("Failed to save");
        } finally {
            setBusy(false);
        }
    };

    const persistQuotes = async (next, successMsg) => {
        setQuotesBusy(true);
        try {
            const s = await api
                .patch("/settings", { quotes: next.map(({ id, text, author }) => ({ id, text, author })) })
                .then((r) => r.data);
            hydrate(s);
            if (successMsg) toast.success(successMsg);
        } catch (e) {
            toast.error("Failed to save quotes");
        } finally {
            setQuotesBusy(false);
        }
    };

    const addQuote = async () => {
        const text = newQuote.text.trim();
        if (!text) {
            toast.error("Quote text is required");
            return;
        }
        const next = [...quotes, { text, author: newQuote.author.trim() }];
        await persistQuotes(next, "Quote added");
        setNewQuote({ text: "", author: "" });
    };

    const removeQuote = async (idx) => {
        const next = quotes.filter((_, i) => i !== idx);
        await persistQuotes(next, "Quote removed");
    };

    const updateQuote = (idx, patch) => {
        setQuotes((qs) => qs.map((q, i) => (i === idx ? { ...q, ...patch } : q)));
    };

    const saveEdits = async () => {
        await persistQuotes(quotes, "Quotes saved");
    };

    const restoreDefaults = async () => {
        setQuotesBusy(true);
        try {
            const defaults = DEFAULT_FALLBACK.map((q) => ({ ...q }));
            const s = await api.patch("/settings", { quotes: defaults }).then((r) => r.data);
            hydrate(s);
            toast.success("Defaults restored");
        } catch (e) {
            toast.error("Failed to restore defaults");
        } finally {
            setQuotesBusy(false);
        }
    };

    const saveSignature = async () => {
        setSignatureBusy(true);
        try {
            const s = await api.patch("/settings", { signature_html: signature }).then((r) => r.data);
            hydrate(s);
            toast.success("Signature saved");
        } catch (e) {
            toast.error("Failed to save signature");
        } finally {
            setSignatureBusy(false);
        }
    };

    // Agency logo — used on the submission-list PDF sent to the author.
    const [logoBusy, setLogoBusy] = useState(false);
    const [logoCacheBust, setLogoCacheBust] = useState(0);
    const uploadAgencyLogo = async (file) => {
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        setLogoBusy(true);
        try {
            const s = await api.post("/settings/agency_logo", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            }).then((r) => r.data);
            hydrate(s);
            setLogoCacheBust(Date.now());
            toast.success("Agency logo saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to save logo");
        } finally {
            setLogoBusy(false);
        }
    };
    const removeAgencyLogo = async () => {
        if (!confirm("Remove the agency logo from the submission-list PDF?")) return;
        setLogoBusy(true);
        try {
            const s = await api.delete("/settings/agency_logo").then((r) => r.data);
            hydrate(s);
            setLogoCacheBust(Date.now());
            toast.success("Agency logo removed");
        } catch (e) {
            toast.error("Failed to remove logo");
        } finally {
            setLogoBusy(false);
        }
    };

    if (!settings) return <div className="pulse-mono font-mono text-sm text-muted-foreground">Loading…</div>;

    return (
        <div>
            <PageHeader
                eyebrow="Preferences"
                title="Settings"
                description="Configure your sender identity, connect Resend so the app can send emails on your behalf, and curate the quotes on your dashboard."
                testId="settings-header"
            />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 paper border border-border rounded-sm p-6 space-y-5" data-testid="settings-form">
                    <Field label="Your name">
                        <input
                            className="input-flat"
                            value={form.sender_name}
                            onChange={(e) => setForm({ ...form, sender_name: e.target.value })}
                            placeholder="e.g. Abigail Samoun"
                            data-testid="settings-sender-name"
                        />
                    </Field>
                    <Field label="Sender email (must be verified with Resend)">
                        <input
                            className="input-flat"
                            value={form.sender_email}
                            onChange={(e) => setForm({ ...form, sender_email: e.target.value })}
                            placeholder="you@yourdomain.com"
                            data-testid="settings-sender-email"
                        />
                    </Field>
                    <Field label="Default sign-off">
                        <input
                            className="input-flat"
                            value={form.default_signoff}
                            onChange={(e) => setForm({ ...form, default_signoff: e.target.value })}
                            data-testid="settings-signoff"
                        />
                    </Field>
                    <Field label="Editor email subject template">
                        <input
                            className="input-flat font-mono"
                            value={form.email_subject_template}
                            onChange={(e) => setForm({ ...form, email_subject_template: e.target.value })}
                            data-testid="settings-subject-template"
                            placeholder="Red Fox Literary/{TITLE}"
                        />
                        <div className="mt-2 text-xs text-muted-foreground">
                            Used for every editor submission email. Placeholders (uppercase versions render in ALL CAPS):{" "}
                            <code className="font-mono">{"{title}"}</code>/<code className="font-mono">{"{TITLE}"}</code>,{" "}
                            <code className="font-mono">{"{author}"}</code>/<code className="font-mono">{"{AUTHOR}"}</code>,{" "}
                            <code className="font-mono">{"{category}"}</code>/<code className="font-mono">{"{CATEGORY}"}</code>.
                        </div>
                    </Field>
                    <Field label="Resend API key">
                        <div className="flex gap-2">
                            <input
                                className="input-flat font-mono"
                                type="password"
                                value={form.resend_api_key}
                                onChange={(e) => setForm({ ...form, resend_api_key: e.target.value })}
                                placeholder={settings.resend_api_key_set ? "•••• already set — paste to replace" : "re_..."}
                                data-testid="settings-resend-key"
                            />
                            <a
                                href="https://resend.com/api-keys"
                                target="_blank"
                                rel="noreferrer"
                                className="btn-ghost text-xs"
                                data-testid="settings-resend-link"
                            >
                                <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} /> Get key
                            </a>
                        </div>
                        <div className="mt-2 text-xs">
                            Status:{" "}
                            <span className={settings.resend_api_key_set ? "text-[#1B362F]" : "text-[#8C2D19]"}>
                                {settings.resend_api_key_set ? "Configured" : "Not configured"}
                            </span>
                        </div>
                    </Field>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <TestSendButton
                            senderEmailConfigured={!!settings.resend_api_key_set && !!form.sender_email}
                            defaultTo={form.sender_email}
                        />
                        <button className="btn-primary disabled:opacity-60" onClick={save} disabled={busy} data-testid="settings-save">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" strokeWidth={1.5} />}
                            Save
                        </button>
                    </div>
                </div>

                <aside className="paper border border-border rounded-sm p-6" data-testid="settings-notes">
                    <div className="label-mono mb-2">Notes</div>
                    <ol className="text-sm space-y-3 list-decimal list-inside text-foreground/80">
                        <li>
                            Sign up at <a href="https://resend.com" target="_blank" rel="noreferrer" className="underline">resend.com</a>{" "}
                            (free tier available).
                        </li>
                        <li>Verify your sending domain or use their sandbox sender.</li>
                        <li>Create an API key and paste it here.</li>
                        <li>Emails will be sent from the address you enter — make sure it&apos;s verified.</li>
                    </ol>
                    <div className="mt-6 text-xs text-muted-foreground flex items-start gap-2">
                        <KeyRound className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={1.5} />
                        <div>Your Resend key is stored locally on the server and never displayed after save.</div>
                    </div>
                </aside>
            </div>

            {/* Gmail connection panel */}
            <section className="mt-10 paper border border-border rounded-sm p-6" data-testid="settings-gmail-panel">
                <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                    <div>
                        <div className="label-mono mb-1 flex items-center gap-2">
                            <MailIcon className="w-3.5 h-3.5" strokeWidth={1.5} /> Send via Gmail
                        </div>
                        <h2 className="font-serif text-2xl leading-tight">Save drafts to Gmail</h2>
                        <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                            Instead of sending through Resend, hand each email off to Gmail as a draft — attachments and formatting included.
                            You review and press Send from Gmail itself, so submissions land in your Sent folder (and any Gmail-monitoring tools you use see them).
                        </p>
                    </div>
                    <div className="shrink-0">
                        {gmailStatus?.connected ? (
                            <div className="text-right">
                                <div className="label-mono text-[#1B362F] mb-1">Connected</div>
                                <div className="font-mono text-xs text-muted-foreground mb-2" data-testid="gmail-connected-email">
                                    {gmailStatus.email || "(unknown account)"}
                                </div>
                                <button
                                    className="btn-ghost text-xs"
                                    onClick={disconnectGmail}
                                    disabled={gmailBusy}
                                    data-testid="gmail-disconnect-btn"
                                >
                                    <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} /> Disconnect
                                </button>
                            </div>
                        ) : (
                            <button
                                className="btn-primary disabled:opacity-60"
                                onClick={connectGmail}
                                disabled={gmailBusy}
                                data-testid="gmail-connect-btn"
                            >
                                {gmailBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MailIcon className="w-4 h-4" strokeWidth={1.5} />}
                                Connect Gmail
                            </button>
                        )}
                    </div>
                </div>
                <p className="text-xs text-muted-foreground">
                    Google will only grant permission to create Gmail drafts on your behalf (scope <span className="font-mono">gmail.compose</span>) — the app cannot read your inbox or send emails without your explicit click in Gmail.
                </p>
            </section>

            {/* Email signature panel */}
            <section className="mt-10 paper border border-border rounded-sm p-6" data-testid="settings-signature-panel">
                <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                    <div>
                        <div className="label-mono mb-1 flex items-center gap-2">
                            <PenLine className="w-3.5 h-3.5" strokeWidth={1.5} /> Email signature
                        </div>
                        <h2 className="font-serif text-2xl leading-tight">Your standard signature</h2>
                        <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                            Automatically appended to every editor email you generate. Use the toolbar to add italics,
                            bold, links (email/website/lookbook), and images (logo + book cover grid + lookbook cover).
                            Keep each image under ~400&nbsp;KB to keep emails snappy.
                        </p>
                    </div>
                    <button
                        className="btn-primary disabled:opacity-60 shrink-0"
                        onClick={saveSignature}
                        disabled={signatureBusy}
                        data-testid="signature-save-btn"
                    >
                        {signatureBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" strokeWidth={1.5} />}
                        Save signature
                    </button>
                </div>
                <RichEditor
                    value={signature}
                    onChange={setSignature}
                    enableImages
                    maxImageSizeKb={400}
                    minHeight={280}
                    placeholder="Paste or type your signature block. Use the image button to add the Red Fox Literary logo and book cover images."
                    dataTestId="signature-editor"
                />
                <p className="text-xs text-muted-foreground mt-3">
                    Tip: for the Lookbook link, click on the link icon in the toolbar and paste{" "}
                    <code className="font-mono">https://redfoxliterary.com/lookbook/</code>. For your email link, use{" "}
                    <code className="font-mono">mailto:abigail@redfoxliterary.com</code>.
                </p>
            </section>

            {/* Agency logo — used on the submission-list PDF report */}
            <section className="mt-10 paper border border-border rounded-sm p-6" data-testid="settings-logo-panel">
                <div className="flex items-start justify-between gap-4 mb-5">
                    <div>
                        <div className="label-mono mb-1 flex items-center gap-2">
                            <ImageIcon className="w-3.5 h-3.5" strokeWidth={1.5} /> Agency logo
                        </div>
                        <h2 className="font-serif text-2xl leading-tight">Report letterhead</h2>
                        <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                            Appears at the top-left of the submission-list PDF you send to authors. PNG, JPG, WEBP, or GIF up to 4&nbsp;MB. A transparent PNG at ~1&nbsp;inch tall looks best.
                        </p>
                    </div>
                </div>

                <div className="flex items-start gap-6 flex-wrap">
                    {settings.agency_logo_path ? (
                        <div className="border border-border rounded-sm bg-white p-3" data-testid="agency-logo-preview">
                            <img
                                src={`${api.defaults.baseURL || ""}/settings/agency_logo?v=${logoCacheBust || settings.agency_logo_path}`}
                                alt="Agency logo preview"
                                className="max-h-24 max-w-[240px] object-contain"
                            />
                        </div>
                    ) : (
                        <div className="border border-dashed border-border rounded-sm p-6 text-xs text-muted-foreground font-mono" data-testid="agency-logo-empty">
                            No logo uploaded yet.
                        </div>
                    )}

                    <div className="flex flex-col gap-2">
                        <label className="btn-primary cursor-pointer">
                            {logoBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" strokeWidth={1.5} />}
                            {settings.agency_logo_path ? "Replace logo" : "Upload logo"}
                            <input
                                type="file"
                                accept="image/png,image/jpeg,image/webp,image/gif"
                                className="hidden"
                                onChange={(e) => {
                                    const f = e.target.files?.[0];
                                    e.target.value = "";
                                    if (f) uploadAgencyLogo(f);
                                }}
                                data-testid="agency-logo-upload"
                                disabled={logoBusy}
                            />
                        </label>
                        {settings.agency_logo_path && (
                            <button
                                type="button"
                                className="btn-ghost text-xs disabled:opacity-60"
                                onClick={removeAgencyLogo}
                                disabled={logoBusy}
                                data-testid="agency-logo-remove"
                            >
                                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Remove
                            </button>
                        )}
                    </div>
                </div>
            </section>

            {/* Client (author) directory */}
            <ClientsPanel />

            {/* Inspirational quotes panel */}
            <section className="mt-10 paper border border-border rounded-sm p-6" data-testid="settings-quotes-panel">
                <div className="flex items-start justify-between gap-4 mb-5">
                    <div>
                        <div className="label-mono mb-1 flex items-center gap-2">
                            <QuoteIcon className="w-3.5 h-3.5" strokeWidth={1.5} /> Dashboard quotes
                        </div>
                        <h2 className="font-serif text-2xl leading-tight">Inspirational quotes</h2>
                        <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                            These rotate on your Dashboard hero and in the sidebar. Add your favorites, remove any you&apos;re
                            tired of, or restore the built-in set at any time.
                        </p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                        <button
                            className="btn-ghost text-xs"
                            onClick={restoreDefaults}
                            disabled={quotesBusy}
                            data-testid="quotes-restore-defaults"
                            title="Replace list with the built-in defaults"
                        >
                            <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.5} /> Restore defaults
                        </button>
                        <button
                            className="btn-primary disabled:opacity-60"
                            onClick={saveEdits}
                            disabled={quotesBusy}
                            data-testid="quotes-save-edits"
                        >
                            {quotesBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" strokeWidth={1.5} />}
                            Save edits
                        </button>
                    </div>
                </div>

                {/* Add a new quote */}
                <div className="border border-border rounded-sm p-4 bg-secondary/30 mb-6" data-testid="quotes-add-row">
                    <div className="label-mono mb-2">Add a new quote</div>
                    <div className="grid grid-cols-1 md:grid-cols-[1fr_240px_auto] gap-3">
                        <input
                            className="input-flat"
                            placeholder="&ldquo;A book is a garden…&rdquo;"
                            value={newQuote.text}
                            onChange={(e) => setNewQuote({ ...newQuote, text: e.target.value })}
                            data-testid="quotes-new-text"
                        />
                        <input
                            className="input-flat"
                            placeholder="Author (optional)"
                            value={newQuote.author}
                            onChange={(e) => setNewQuote({ ...newQuote, author: e.target.value })}
                            data-testid="quotes-new-author"
                            onKeyDown={(e) => {
                                if (e.key === "Enter") addQuote();
                            }}
                        />
                        <button
                            className="btn-primary disabled:opacity-60"
                            onClick={addQuote}
                            disabled={quotesBusy || !newQuote.text.trim()}
                            data-testid="quotes-add-btn"
                        >
                            <Plus className="w-4 h-4" strokeWidth={1.5} /> Add
                        </button>
                    </div>
                </div>

                {/* Existing quotes list */}
                {quotes.length === 0 ? (
                    <div className="text-sm text-muted-foreground border border-dashed border-border rounded-sm p-6 text-center" data-testid="quotes-empty">
                        No quotes yet. Add one above, or restore the defaults.
                    </div>
                ) : (
                    <ul className="divide-y divide-border border border-border rounded-sm" data-testid="quotes-list">
                        {quotes.map((q, idx) => (
                            <li
                                key={q.id || `${idx}-${q.text.slice(0, 20)}`}
                                className="grid grid-cols-1 md:grid-cols-[1fr_240px_auto] gap-3 items-start p-4"
                                data-testid={`quote-row-${idx}`}
                            >
                                <textarea
                                    className="input-flat font-serif italic leading-relaxed min-h-[52px]"
                                    value={q.text}
                                    rows={2}
                                    onChange={(e) => updateQuote(idx, { text: e.target.value })}
                                    data-testid={`quote-text-${idx}`}
                                />
                                <input
                                    className="input-flat"
                                    placeholder="— Author"
                                    value={q.author || ""}
                                    onChange={(e) => updateQuote(idx, { author: e.target.value })}
                                    data-testid={`quote-author-${idx}`}
                                />
                                <button
                                    className="btn-ghost text-xs text-[#8C2D19] hover:bg-[#8C2D19]/10"
                                    onClick={() => removeQuote(idx)}
                                    disabled={quotesBusy}
                                    data-testid={`quote-remove-${idx}`}
                                    title="Remove this quote"
                                >
                                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Remove
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
                <p className="mt-3 text-xs text-muted-foreground">
                    Tip: edits above are held locally until you press <span className="font-mono">Save edits</span>. Adding or
                    removing saves immediately.
                </p>
            </section>
        </div>
    );
}

// Kept in sync with backend DEFAULT_QUOTES so "Restore defaults" works even after we've cleared them.
const DEFAULT_FALLBACK = [
    { text: "Life shrinks or expands in proportion to one's courage.", author: "Anaïs Nin" },
    { text: "Words are animals alive with a will of their own.", author: "Carl Jung" },
    { text: "A book is a garden, an orchard, a storehouse, a party, a company by the way, a counselor, a multitude of counselors.", author: "Henry Ward Beecher" },
    { text: "There is no greater agony than bearing an untold story inside you.", author: "Maya Angelou" },
    { text: "Read, read, read. Read everything — trash, classics, good and bad, and see how they do it.", author: "William Faulkner" },
    { text: "You can make anything by writing.", author: "C. S. Lewis" },
    { text: "Books are a uniquely portable magic.", author: "Stephen King" },
    { text: "We read to know we are not alone.", author: "William Nicholson" },
];

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="label-mono">{label}</span>
            <div className="mt-2">{children}</div>
        </label>
    );
}

// ---------------------------------------------------------------------------
// Test-send button: one-click "email myself a preview" using the current
// Resend key + verified sender + saved signature. Confirms the whole pipeline
// without needing a real project.
// ---------------------------------------------------------------------------
function TestSendButton({ senderEmailConfigured, defaultTo }) {
    const [open, setOpen] = useState(false);
    const [to, setTo] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open) setTo(defaultTo || "");
    }, [open, defaultTo]);

    const send = async () => {
        const dest = (to || "").trim();
        if (!dest) return toast.error("Enter an email address");
        setBusy(true);
        try {
            const res = await api.post("/settings/test_send", { to: dest }).then((r) => r.data);
            toast.success(`Test email sent to ${res.to}${res.resend_id ? " · " + res.resend_id : ""}`);
            setOpen(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Test send failed");
        } finally {
            setBusy(false);
        }
    };

    if (!open) {
        return (
            <button
                className="btn-ghost text-xs disabled:opacity-60"
                onClick={() => setOpen(true)}
                disabled={!senderEmailConfigured}
                title={
                    senderEmailConfigured
                        ? "Send yourself a preview email to verify the pipeline (Resend key, sender, signature)"
                        : "Save your Resend key and Sender email first"
                }
                data-testid="settings-test-send-btn"
            >
                <Send className="w-3.5 h-3.5" strokeWidth={1.5} /> Send test email
            </button>
        );
    }

    return (
        <div
            className="flex items-center gap-2 border border-border rounded-sm bg-secondary/40 p-2"
            data-testid="settings-test-send-row"
        >
            <input
                className="input-flat text-sm font-mono w-64"
                type="email"
                placeholder="you@example.com"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                autoFocus
                data-testid="settings-test-send-to"
            />
            <button
                className="btn-primary text-xs disabled:opacity-60"
                onClick={send}
                disabled={busy || !to.trim()}
                data-testid="settings-test-send-submit"
            >
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" strokeWidth={1.5} />}
                {busy ? "Sending…" : "Send"}
            </button>
            <button
                className="btn-ghost text-xs"
                onClick={() => setOpen(false)}
                disabled={busy}
                data-testid="settings-test-send-cancel"
            >
                Cancel
            </button>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Clients (author directory) panel
// ---------------------------------------------------------------------------
function ClientsPanel() {
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [importBusy, setImportBusy] = useState(false);
    const [addBusy, setAddBusy] = useState(false);
    const [newClient, setNewClient] = useState({ name: "", email: "" });
    const [importMode, setImportMode] = useState("upsert");
    const [importResult, setImportResult] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const list = await api.get("/clients").then((r) => r.data);
            setClients(list || []);
        } catch (e) {
            toast.error("Failed to load clients");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const onFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setImportBusy(true);
        setImportResult(null);
        try {
            const fd = new FormData();
            fd.append("file", f);
            const res = await api
                .post(`/clients/import?mode=${encodeURIComponent(importMode)}`, fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                })
                .then((r) => r.data);
            setImportResult(res);
            toast.success(
                `Imported ${res.inserted} new, ${res.updated} updated, ${res.skipped} skipped`
            );
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Import failed");
        } finally {
            setImportBusy(false);
            e.target.value = "";
        }
    };

    const addOne = async () => {
        const name = newClient.name.trim();
        if (!name) return toast.error("Name is required");
        setAddBusy(true);
        try {
            await api.post("/clients", { name, email: newClient.email.trim() });
            setNewClient({ name: "", email: "" });
            toast.success("Client added");
            load();
        } catch (e) {
            toast.error("Failed to add");
        } finally {
            setAddBusy(false);
        }
    };

    const removeOne = async (id) => {
        if (!window.confirm("Remove this client from your directory?")) return;
        try {
            await api.delete(`/clients/${id}`);
            load();
        } catch (e) {
            toast.error("Failed to remove");
        }
    };

    return (
        <section className="mt-10 paper border border-border rounded-sm p-6" data-testid="settings-clients-panel">
            <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                <div>
                    <div className="label-mono mb-1 flex items-center gap-2">
                        <Users className="w-3.5 h-3.5" strokeWidth={1.5} /> Client directory
                    </div>
                    <h2 className="font-serif text-2xl leading-tight">Authors &amp; client emails</h2>
                    <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                        Upload a CSV of your clients (name + email). Whenever you type an author name
                        into a project sidebar, the app will auto-fill their email from this list.
                    </p>
                </div>
                <div className="shrink-0 flex items-center gap-2">
                    <select
                        className="input-flat text-xs"
                        value={importMode}
                        onChange={(e) => setImportMode(e.target.value)}
                        title="Import mode"
                        data-testid="clients-import-mode"
                    >
                        <option value="upsert">Upsert (update matching names)</option>
                        <option value="add_new">Add new only</option>
                        <option value="replace">Replace all</option>
                    </select>
                    <label className="btn-primary cursor-pointer disabled:opacity-60">
                        {importBusy ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <Upload className="w-4 h-4" strokeWidth={1.5} />
                        )}
                        Import CSV
                        <input
                            type="file"
                            accept=".csv,text/csv"
                            className="hidden"
                            onChange={onFile}
                            disabled={importBusy}
                            data-testid="clients-csv-input"
                        />
                    </label>
                </div>
            </div>

            <div className="text-xs text-muted-foreground mb-4">
                <span className="font-mono">Recognised columns:</span> <code>Name</code> /
                <code> Author</code> / <code>Full Name</code>, and <code>Email</code> /
                <code> Email Address</code>. Extras like <code>Notes</code> are optional.
            </div>

            {importResult && (
                <div
                    className="border border-border rounded-sm p-3 bg-secondary/30 text-xs font-mono mb-4"
                    data-testid="clients-import-result"
                >
                    Import complete — {importResult.inserted} inserted, {importResult.updated} updated,{" "}
                    {importResult.skipped} skipped ({importResult.total_in_db} total in directory).
                </div>
            )}

            {/* Manual add row */}
            <div className="border border-border rounded-sm p-4 bg-secondary/30 mb-6" data-testid="clients-add-row">
                <div className="label-mono mb-2">Add a client manually</div>
                <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3">
                    <input
                        className="input-flat"
                        placeholder="Author name"
                        value={newClient.name}
                        onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                        data-testid="clients-new-name"
                    />
                    <input
                        className="input-flat font-mono"
                        type="email"
                        placeholder="author@example.com"
                        value={newClient.email}
                        onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                        data-testid="clients-new-email"
                    />
                    <button
                        className="btn-primary disabled:opacity-60"
                        onClick={addOne}
                        disabled={addBusy || !newClient.name.trim()}
                        data-testid="clients-add-btn"
                    >
                        <Plus className="w-4 h-4" strokeWidth={1.5} /> Add
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="text-sm text-muted-foreground">Loading…</div>
            ) : clients.length === 0 ? (
                <div className="text-sm text-muted-foreground border border-dashed border-border rounded-sm p-6 text-center" data-testid="clients-empty">
                    No clients yet. Import a CSV or add one above.
                </div>
            ) : (
                <ul className="divide-y divide-border border border-border rounded-sm max-h-[400px] overflow-auto" data-testid="clients-list">
                    {clients.map((c) => (
                        <li
                            key={c.id}
                            className="grid grid-cols-[1fr_1fr_auto] gap-3 items-center p-3 text-sm"
                            data-testid={`client-row-${c.id}`}
                        >
                            <span className="font-serif">{c.name}</span>
                            <span className="font-mono text-xs text-muted-foreground truncate">{c.email || "—"}</span>
                            <button
                                className="btn-ghost text-xs text-[#8C2D19] hover:bg-[#8C2D19]/10"
                                onClick={() => removeOne(c.id)}
                                data-testid={`client-remove-${c.id}`}
                                title="Remove"
                            >
                                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
            {clients.length > 0 && (
                <div className="mt-2 text-xs text-muted-foreground font-mono">{clients.length} client(s)</div>
            )}
        </section>
    );
}
